import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import CreateSubscription from './CreateSubscription';

const SubscriptionsList = ({ onSubscriptionSelect, onRefresh }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, [statusFilter, packageFilter]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      let url = '/admin/subscriptions?';
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (packageFilter) params.append('package', packageFilter);
      url += params.toString();

      const data = await apiRequest(url);
      if (data && data.success) {
        setSubscriptions(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    clearTimeout(window.subscriptionSearchTimeout);
    window.subscriptionSearchTimeout = setTimeout(() => {
      fetchSubscriptions();
    }, 500);
  };

  const getStatusBadge = (status, endDate) => {
    const isExpired = new Date(endDate) < new Date();
    if (status === 'cancelled') {
      return <span className="badge badge-danger">ملغي</span>;
    }
    if (status === 'expired' || isExpired) {
      return <span className="badge badge-danger">منتهي</span>;
    }
    if (status === 'active') {
      return <span className="badge badge-success">نشط</span>;
    }
    return <span className="badge badge-info">قيد الانتظار</span>;
  };

  const getPackageLabel = (pkg) => {
    const labels = {
      basic: 'أساسي',
      standard: 'قياسي',
      premium: 'مميز'
    };
    return labels[pkg] || pkg;
  };

  if (loading && subscriptions.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div>
      {showCreateForm ? (
        <CreateSubscription
          onSubscriptionCreated={(newSubscription) => {
            setShowCreateForm(false);
            fetchSubscriptions();
            if (onRefresh) onRefresh();
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      ) : (
        <>
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">قائمة الاشتراكات</h2>
              <div className="flex gap-2">
                <button className="btn btn-success btn-sm" onClick={() => setShowCreateForm(true)}>
                  ➕ إضافة اشتراك جديد
                </button>
                <button className="btn btn-primary btn-sm" onClick={fetchSubscriptions}>
                  🔄 تحديث
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="بحث عن اشتراك..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <select
                  className="form-control"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">جميع الحالات</option>
                  <option value="active">نشط</option>
                  <option value="expired">منتهي</option>
                  <option value="cancelled">ملغي</option>
                  <option value="pending">قيد الانتظار</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <select
                  className="form-control"
                  value={packageFilter}
                  onChange={(e) => setPackageFilter(e.target.value)}
                >
                  <option value="">جميع الباقات</option>
                  <option value="basic">أساسي</option>
                  <option value="standard">قياسي</option>
                  <option value="premium">مميز</option>
                </select>
              </div>
            </div>

            {subscriptions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">لا يوجد اشتراكات</div>
                <div className="empty-state-text">ابدأ بإضافة اشتراك جديد</div>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>العميل</th>
                      <th>البريد الإلكتروني</th>
                      <th>الباقة</th>
                      <th>الحالة</th>
                      <th>تاريخ البدء</th>
                      <th>تاريخ الانتهاء</th>
                      <th>التجديد التلقائي</th>
                      <th>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((subscription) => (
                      <tr key={subscription._id}>
                        <td><strong>{subscription.customerId?.name || '—'}</strong></td>
                        <td>{subscription.customerId?.email || '—'}</td>
                        <td>
                          <span className="badge badge-info">
                            {getPackageLabel(subscription.package)}
                          </span>
                        </td>
                        <td>{getStatusBadge(subscription.status, subscription.endDate)}</td>
                        <td>{new Date(subscription.startDate).toLocaleDateString('ar-SA')}</td>
                        <td>{new Date(subscription.endDate).toLocaleDateString('ar-SA')}</td>
                        <td>
                          {subscription.autoRenew ? (
                            <span className="badge badge-success">نعم</span>
                          ) : (
                            <span className="badge badge-secondary">لا</span>
                          )}
                        </td>
                        <td>
                          {onSubscriptionSelect && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => onSubscriptionSelect(subscription)}
                            >
                              عرض التفاصيل
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionsList;
